import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { ROUTES } from '@shared/utils/constants';
import { Button } from '@shared/components/ui/UI';
import IqarxLogo from '@assets/svg/iqarx.svg';
import QicardLogo from '@assets/svg/Qi.svg';

// Confetti Animation Component
const Confetti = () => {
  const colors = ['#6366f1', '#8b5cf6', '#d946ef', '#f59e0b', '#10b981', '#3b82f6'];
  
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            left: `${Math.random() * 100}%`,
          }}
          initial={{
            y: -20,
            x: 0,
            rotate: 0,
            opacity: 1,
          }}
          animate={{
            y: window.innerHeight + 100,
            x: (Math.random() - 0.5) * 200,
            rotate: Math.random() * 720,
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            delay: Math.random() * 2,
            ease: 'easeIn',
          }}
        />
      ))}
    </div>
  );
};

// Success Animation
const SuccessAnimation = () => {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.2,
      }}
      className="relative"
    >
      {/* Background glow */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="absolute inset-0 bg-green-400 rounded-full blur-3xl"
      />
      
      {/* Check circle */}
      <div className="relative w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
        <motion.svg
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="w-16 h-16 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <motion.path
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </motion.svg>
      </div>
    </motion.div>
  );
};

// Stats Card
const StatCard = ({ label, value, icon, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-2xl p-6 shadow-soft text-center"
    >
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </motion.div>
  );
};

const CompletedPage = () => {
  const navigate = useNavigate();
  const { state, actions } = useInterview();
  const [showConfetti, setShowConfetti] = useState(true);

  // Text content
  const text = {
    title: 'تهانينا!',
    subtitle: 'لقد أكملت المقابلة بنجاح',
    description: 'تم حفظ مقابلتك وسيقوم فريقنا بمراجعتها قريباً. ستتلقى إشعاراً بالنتائج عبر البريد الإلكتروني.',
    duration: 'مدة المقابلة',
    questions: 'الأسئلة',
    responses: 'الردود',
    newInterview: 'مقابلة جديدة',
    viewSummary: 'عرض الملخص',
    backToHome: 'العودة للرئيسية',
    thankYou: 'شكراً لاستخدامك منصة المقابلات الذكية',
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get interview stats
  const duration = state.interviewDuration || 0;
  const transcriptCount = state.transcript?.length || 0;

  // Cleanup any lingering media tracks when CompletedPage mounts
  // This is a safety measure in case cleanup didn't complete during navigation
  useEffect(() => {
    const stopAllMediaTracks = () => {
      let stoppedCount = 0;

      // Stop all video/audio elements in the DOM
      const allMediaElements = document.querySelectorAll('video, audio');
      allMediaElements.forEach(el => {
        if (el.srcObject) {
          el.srcObject.getTracks().forEach(track => {
            if (track.readyState === 'live') {
              track.stop();
              stoppedCount++;
              console.log('CompletedPage: Stopped track:', track.kind, track.label);
            }
          });
          el.srcObject = null;
        }
      });

      // Also check the LiveKit audio container if it still exists
      const livekitContainer = document.getElementById('livekit-audio-container');
      if (livekitContainer) {
        livekitContainer.querySelectorAll('audio').forEach(el => {
          if (el.srcObject) {
            el.srcObject.getTracks().forEach(track => {
              track.stop();
              stoppedCount++;
              console.log('CompletedPage: Stopped LiveKit audio track');
            });
            el.srcObject = null;
          }
        });
        livekitContainer.remove();
        console.log('CompletedPage: Removed LiveKit audio container');
      }

      return stoppedCount;
    };

    // Run cleanup immediately
    console.log('CompletedPage: Running media cleanup...');
    let stopped = stopAllMediaTracks();
    console.log('CompletedPage: Initial cleanup stopped', stopped, 'tracks');

    // Run cleanup again after a short delay (in case some tracks weren't ready)
    const timer1 = setTimeout(() => {
      stopped = stopAllMediaTracks();
      if (stopped > 0) {
        console.log('CompletedPage: Delayed cleanup stopped', stopped, 'tracks');
      }
    }, 500);

    // Run cleanup one more time after a longer delay
    const timer2 = setTimeout(() => {
      stopped = stopAllMediaTracks();
      if (stopped > 0) {
        console.log('CompletedPage: Final cleanup stopped', stopped, 'tracks');
      }
    }, 1500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Hide confetti after animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Handle new interview
  const handleNewInterview = () => {
    actions.resetState();
    navigate(ROUTES.LOGIN);
  };

  // Handle back to home
  const handleBackToHome = () => {
    actions.resetState();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-surface-50 via-primary-50/30 to-surface-100"
      dir="rtl"
    >
      {/* Confetti */}
      {showConfetti && <Confetti />}

      {/* Header with logos */}
      <header className="py-6 px-8">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
                        <img
                          src={IqarxLogo}
                          alt="IQARX"
                          className="h-10 w-auto"
                          style={{ filter: 'brightness(0)' }}
                        />       
                        <img
                          src={QicardLogo}
                          alt="Qi"
                          className="h-10 w-auto"
                          style={{ filter: 'brightness(0)' }}
                        />        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center">
          {/* Success Animation */}
          <div className="flex justify-center mb-8">
            <SuccessAnimation />
          </div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold text-gray-900 mb-4 font-display"
          >
            {text.title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl text-gray-600 mb-4"
          >
            {text.subtitle}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-gray-500 max-w-lg mx-auto mb-12"
          >
            {text.description}
          </motion.p>

          {/* Stats */}
          {/* <div className="grid md:grid-cols-3 gap-6 mb-12">
            <StatCard
              label={text.duration}
              value={formatDuration(duration)}
              delay={0.6}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <StatCard
              label={text.questions}
              value={Math.ceil(transcriptCount / 2)}
              delay={0.7}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <StatCard
              label={text.responses}
              value={Math.floor(transcriptCount / 2)}
              delay={0.8}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
            />
          </div> */}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            {/* <Button
              variant="primary"
              size="lg"
              onClick={handleNewInterview}
              className="min-w-[200px]"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {text.newInterview}
              </span>
            </Button> */}
            
            <Button
              variant="secondary"
              size="lg"
              onClick={handleBackToHome}
              className="min-w-[200px]"
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {text.backToHome}
              </span>
            </Button>
          </motion.div>

          {/* Thank you message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-gray-400 text-sm"
          >
            {text.thankYou}
          </motion.p>
        </div>
      </main>

      {/* Decorative elements */}
      <div className="fixed bottom-0 left-0 w-64 h-64 bg-primary-500/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 pointer-events-none" />
      <div className="fixed top-1/2 right-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
};

export default CompletedPage;
